import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { useParams } from "react-router-dom";
import dayjs from "dayjs";
import get from "lodash/get";
import map from "lodash/map";
import filter from "lodash/filter";

import { useForm } from "react-hook-form";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Box from "@mui/material/Box";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Popover from "@mui/material/Popover";
import Stack from "@mui/material/Stack";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { useSnackbar } from "notistack";
import find from "lodash/find";
import omit from "lodash/omit";

import Button from "../components/shared/Button";
import Container from "../components/layout/Container";
import ProjectForm from "../components/documents/ProjectForm";
import HistoryForm from "../components/documents/HistoryForm";
import AdvertiseForm from "../components/documents/AdvertiseForm";
import useSyncFormData from "../components/hooks/useSyncFormData";
import useQueryParams from "../components/hooks/useQueryParams";
import { useSidebarContext } from "../components/context/SidebarContext";
import FullScreenLoader from "../components/shared/FullScreenLoader";
import DatePickerController from "../components/forms/DatePickerController";
import DocumentsService from "../services/DocumentsService";
import RelatedDocumentsList from "../components/documents/RelatedDocumentsList";
import useRelatedDocuments from "../components/hooks/useRelatedDocuments";
import {
  getDocTypesWithTemplate,
  getExtensionFromFilename,
  getRelatedDocTypesExclude,
} from "../helpers/utils";
import DocumentDraftService from "../services/DocumentDraftService";
import useDraft from "../components/hooks/useDraft";
import DocumentPreviewDialog from "../components/estates/DocumentPreviewDialog";
import useSponsorReportData from "../components/hooks/useSponsorReportData";

const TabPanel = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`vertical-tabpanel-${index}`}
      aria-labelledby={`vertical-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
};

const DocumentForm = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { setOpen } = useSidebarContext();
  const [tabIndex, setTabIndex] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [isBuilding, setIsBuilding] = useState(false);
  const [documentType, setDocumentType] = useState();
  const [dates, setDates] = useState({});
  const [additionalUnits, setAdditionalUnits] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [docsOrder, setDocsOrder] = useState([]);
  const [openPreview, setOpenPreview] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState();
  const [isQueryReady, setIsQueryReady] = useState(false);

  const iframeRef = useRef(null);

  const params = useParams();
  const { query, updateQueryParams } = useQueryParams();
  const { data, refetch, isLoading } = useSponsorReportData(
    params.estateId,
    additionalUnits,
    query.get("startDate"),
    query.get("endDate"),
    {
      refetchOnWindowFocus: false,
      enabled: false,
    }
  );

  const draftId = query.get("draft");
  const { data: draftData, isFetching: isFetchingDraft } = useDraft(draftId, {
    refetchOnWindowFocus: false,
    enabled: !!draftId,
  });

  const { data: relatedDocuments, isFetching: isFetchingRelatedDocs } =
    useRelatedDocuments({
      estateId: params.estateId,
      withNoInformation: true,
      isCreated: false,
      documentTypes: getRelatedDocTypesExclude([28]),
      startDate: dayjs(query.get("startDate"), "DD/MM/YYYY"),
      endDate: dayjs(query.get("endDate"), "DD/MM/YYYY"),
      withContent: true,
    });

  const { control, setValue, getValues, watch, register, reset } = useForm({
    defaultValues: {
      project: {
        sponsorLogoId: null,
        date: "",
        name: "",
        address: "",
        country: "",
        owner: {
          firstName: "",
          name: "",
          email: "",
          mobile: "",
        },
      },
      history: {
        calls: 0,
        websiteView: 0,
        websiteViewLabel: [],
        websiteViewData: [],
        leadReceived: {
          promoter: 0,
          whise: 0,
          website: 0,
          total: 0,
        },
        appointments: 0,
        offersReceived: 0,
        acceptedOffers: 0,
        signedCompromises: 0,
        visits: {
          office: 0,
          worksite: 0,
        },
      },
      advertise: {
        digitalAdvertising: [],
        mailingsProject: 0,
        mailingsUnits: 0,
        mailingsAutomatic: 0,
        panel: "false",
        printAdvertising: "false",
      },
      customersFollowUp: [],
    },
  });

  const getFormData = () => {
    const draftPictureUrl = getValues("estatePictureUrl");
    const estatePictureUrl =
      draftPictureUrl || get(data, ["estate", "pictures", 0, "urlLarge"], "");

    const { sponsorLogo: _, ...formValues } = getValues();
    const eventDate = get(formValues, ["advertise", "event"], "");
    let eventDateObj;
    if (!!eventDate) {
      eventDateObj =
        typeof eventDate === "object"
          ? dayjs(eventDate).locale("fr").format("L")
          : dayjs(eventDate, "DD/MM/YYYY").locale("fr").format("L");
    }

    formValues.advertise.event = eventDateObj;

    let relatedDocs = map(docsOrder, (id) =>
      find(relatedDocuments, (doc) => doc.id === id)
    );

    const buildBase64Src = (extension, content) =>
      `data:image/${extension};base64,${content}`;

    relatedDocs = filter(
      map(relatedDocs, (doc) => {
        if (!doc) return null;
        const extension = getExtensionFromFilename(doc.filename);
        const base64 = buildBase64Src(extension, doc.content);

        return {
          ...doc,
          content: base64,
          extension,
        };
      }),
      Boolean
    );

    return {
      ...formValues,
      logoUrl: `${process.env.REACT_APP_URL}new-logo.jpg`,
      pdfIconUrl: `${process.env.REACT_APP_URL}assets/images/preview-pdf-icon.png`,
      estatePictureUrl,
      relatedDocuments: relatedDocs,
    };
  };

  const previewDocument = async () => {
    if (iframeRef.current) {
      const iframeDocument =
        iframeRef.current.contentDocument ||
        iframeRef.current.contentWindow.document;

      iframeDocument.open();
      iframeDocument.close();

      const values = getFormData();

      const html = await DocumentsService.buildHtml(
        values,
        { isPreview: true },
        documentType
      );
      iframeDocument.open();
      iframeDocument.write(html);
      iframeDocument.close();
    }
  };

  const saveDocument = async (saveAfterBuild = false) => {
    try {
      setIsBuilding(true);
      const values = getFormData();
      const html = await DocumentsService.buildHtml(values, null, documentType);
      const documentTypeName = find(
        getDocTypesWithTemplate(),
        (doc) => doc.id === parseInt(documentType)
      )?.name;

      const startDate = dayjs(dates.startDate);
      const endDate = dayjs(dates.endDate);
      const dateStr =
        startDate.month() === endDate.month()
          ? startDate.format("MM-YY")
          : `${startDate.format("MM-YY")}-${endDate.format("MM-YY")}`;
      const documentName = `${documentTypeName}-${dateStr}-${get(
        data,
        ["estate", "name"],
        ""
      )}`;

      const response = await DocumentsService.buildDocument(
        html,
        docsOrder,
        params.estateId,
        documentName,
        documentType,
        startDate,
        saveAfterBuild
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;

      const filename = `${documentName}.pdf`;

      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
    } finally {
      setIsBuilding(false);
    }
  };

  useSyncFormData({
    setValue,
    getValues,
    ownerId: query.get("owner"),
    data,
    previewDocument,
    startDate: dates.startDate,
    watch,
    relatedDocuments,
    query,
  });

  const handleClosePreview = () => {
    setOpenPreview(false);
    setSelectedDoc(null);
  };
  const previewPdfDocument = (doc) => {
    setSelectedDoc(doc);
    setOpenPreview(true);
  };

  const handleChange = (_event, newValue) => {
    setTabIndex(newValue);
  };

  const handleClickSync = (event) => setAnchorEl(event.currentTarget);

  const handleCloseSync = () => setAnchorEl(null);

  const handleClickOpen = () => {
    setOpenModal(true);
  };

  const handleClose = () => {
    setOpenModal(false);
  };

  const onSave = (value) => {
    handleClose();
    saveDocument(value);
  };

  const synchronize = async () => {
    handleCloseSync();
    const startDate = getValues("startDate");
    const endDate = getValues("endDate");
    updateQueryParams({
      startDate: startDate.locale("fr").format("L"),
      endDate: endDate.locale("fr").format("L"),
    });
    await refetch();
  };

  const saveDraft = async () => {
    try {
      const data = getFormData();
      const draftId = query.get("draft");
      const payload = {
        id: draftId,
        estateId: params.estateId,
        owner: query.get("owner"),
        documentTypeId: documentType,
        startDate: data.startDate,
        endDate: data.endDate,
        data: {
          ...data,
          relatedDocuments: docsOrder,
        },
      };
      const res = await DocumentDraftService.upsert(payload);
      if (res.success && res.data) {
        enqueueSnackbar("Votre progression a été enregistré", {
          variant: "success",
          anchorOrigin: {
            horizontal: "right",
            vertical: "top",
          },
        });
        updateQueryParams({
          draft: res.data,
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (isQueryReady) refetch();
  }, [isQueryReady]);

  useEffect(() => {
    if (!!draftData) {
      let { data: draftValues } = draftData;
      draftValues.advertise.event = !!draftValues.advertise.event
        ? dayjs(draftValues.advertise.event, "DD/MM/YYYY")
        : null;
      reset(omit(draftValues, ["relatedDocuments"]));
      setDocsOrder(draftValues.relatedDocuments || []);
      previewDocument();
    }
  }, [draftData]);

  useEffect(() => {
    !!setOpen && setOpen(false);
  }, [setOpen]);

  useEffect(() => {
    setDocumentType(query.get("documentType"));

    const startDate = dayjs(query.get("startDate"), "DD/MM/YYYY");
    const endDate = dayjs(query.get("endDate"), "DD/MM/YYYY");
    setDates({
      startDate: startDate.format("YYYY-MM-DDTHH:mm:ssZ"),
      endDate: endDate.format("YYYY-MM-DDTHH:mm:ssZ"),
    });
    setValue("startDate", startDate);
    setValue("endDate", endDate);

    let selectedUnits = query.get("selectedUnits")?.split(",") ?? [];
    selectedUnits = filter(selectedUnits, Boolean);
    setAdditionalUnits(
      map(selectedUnits, (unitId) => parseInt(unitId)).filter(Boolean)
    );
    setIsQueryReady(true);
  }, [query]);

  useEffect(() => {
    if (!!documentType && !isFetchingRelatedDocs) previewDocument();
  }, [isFetchingRelatedDocs, relatedDocuments]);

  const open = Boolean(anchorEl);
  const id = open ? "sync-data-popover" : undefined;

  const title = !!data ? (
    <StyledTitle className="d-flex align-items-center justify-content-between">
      <div className="estate-title d-flex align-items-center justify-content-center">
        <span>{get(data, ["estate", "purposeStatus", "name"], "")}</span>
        <span>{get(data, ["estate", "name"], "")}</span>
      </div>
      <div className="btn-container d-flex">
        <Button
          color="danger"
          variant="transparent"
          icon={<img src="/assets/icons/quit.svg" />}
        >
          Quitter
        </Button>
        <Button
          color="success"
          variant="transparent"
          icon={<img src="/assets/icons/sync.svg" />}
          onClick={handleClickSync}
        >
          Synchroniser les données
        </Button>
        <Button
          color="primary"
          variant="contained"
          icon={<img src="/assets/icons/save.svg" />}
          onClick={saveDraft}
        >
          Enregister
        </Button>
      </div>
    </StyledTitle>
  ) : undefined;

  return (
    <Container
      navbarProps={{
        showBackBtn: true,
        title,
        sx: {
          justifyContent: "justify-content-start",
        },
      }}
    >
      <FullScreenLoader open={isLoading || isBuilding} />
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleCloseSync}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <Stack sx={{ p: 3 }} gap={2}>
          <Typography variant="h6">
            Période de synchronisation des données
          </Typography>
          <Stack gap={1}>
            <label className="form-label">Choisissez la période</label>
            <Stack flexDirection="row" gap={1}>
              <DatePickerController
                name="startDate"
                control={control}
                label="Date de début"
                maxDate={getValues("endDate")}
              />
              <DatePickerController
                name="endDate"
                control={control}
                label="Date de fin"
                minDate={getValues("startDate")}
              />
            </Stack>
          </Stack>
          <Stack justifyContent="flex-end">
            <Button variant="outlined" color="primary" onClick={synchronize}>
              Appliquer
            </Button>
          </Stack>
        </Stack>
      </Popover>
      <StyleWrapper className="d-flex">
        <div className="iframe-container">
          <iframe ref={iframeRef} className="h-100" />
        </div>
        <div className="form-container">
          <Tabs
            value={tabIndex}
            onChange={handleChange}
            className="form-tabs"
            TabIndicatorProps={{
              className: "form-tab-indicator",
            }}
          >
            <Tab
              className="form-tab"
              label="Informations du document"
              disableRipple
            />
            {/* <Tab className="form-tab" label="Élements associés" disableRipple /> */}
          </Tabs>
          <div className="form-tab-content">
            <TabPanel value={tabIndex} index={0}>
              <Accordion className="form-tab-content__section" defaultExpanded>
                <AccordionSummary
                  className="accordion-head"
                  expandIcon={<ExpandMoreIcon />}
                >
                  <Typography component="span">
                    Informations du projet
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <ProjectForm
                    control={control}
                    register={register}
                    setValue={setValue}
                    estateId={params.estateId}
                    defaultSelectedDoc={get(
                      draftData,
                      "data.project.sponsorLogoId"
                    )}
                  />
                </AccordionDetails>
              </Accordion>
              <Accordion className="form-tab-content__section">
                <AccordionSummary
                  className="accordion-head"
                  expandIcon={<ExpandMoreIcon />}
                >
                  <Typography component="span">Historique</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <HistoryForm control={control} />
                </AccordionDetails>
              </Accordion>
              <Accordion className="form-tab-content__section">
                <AccordionSummary
                  className="accordion-head"
                  expandIcon={<ExpandMoreIcon />}
                >
                  <Typography component="span">Publicité</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <AdvertiseForm control={control} />
                </AccordionDetails>
              </Accordion>
              <Accordion className="form-tab-content__section">
                <AccordionSummary
                  className="accordion-head"
                  expandIcon={<ExpandMoreIcon />}
                >
                  <Typography component="span">Documents associés</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {!isFetchingDraft && !isFetchingRelatedDocs && (
                    <RelatedDocumentsList
                      relatedDocuments={relatedDocuments}
                      docsOrder={docsOrder}
                      setOrder={setDocsOrder}
                      isDraft={!!draftData}
                      previewPdf={previewPdfDocument}
                    />
                  )}
                </AccordionDetails>
              </Accordion>
              <div className="d-flex justify-content-end gap-3 mt-4">
                <Button
                  color="success"
                  variant="contained"
                  onClick={previewDocument}
                >
                  Appliquer
                </Button>
                <Button
                  color="primary"
                  variant="contained"
                  onClick={handleClickOpen}
                >
                  Imprimer
                </Button>
              </div>
            </TabPanel>
            {/* <TabPanel value={tabIndex} index={1}>
              Item Two
            </TabPanel> */}
          </div>
        </div>
        <DocumentPreviewDialog
          open={openPreview}
          handleClose={handleClosePreview}
          documentInfo={selectedDoc}
        />
      </StyleWrapper>
      <Dialog
        open={openModal}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          Voulez-vous uploader ce document dans la fiche bien ?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Une version PDF sera enregistrée dans la section "Documents créés"
            du bien dans votre application SmartDoc. Notez que ce document ne
            sera pas éditable. Pour sauvegarder votre document en vue de le
            finaliser plus tard, veuillez utiliser le bouton enregistrer en haut
            à droite de la page.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => onSave(false)}>Ne pas uploader</Button>
          <Button
            color="primary"
            variant="contained"
            onClick={() => onSave(true)}
            autoFocus
          >
            Uploader
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

const StyledTitle = styled.div`
  width: 100%;
  padding-left: 50px;

  .estate-title {
    gap: 20px;
    span:nth-child(1) {
      border-radius: 6px;
      font-size: 13px;
      color: var(--white);
      padding: 2px 10px;
      background-color: #003da6;
    }
    span:nth-child(2) {
      font-size: 26px;
      font-weight: 700;
      text-transform: uppercase;
    }
  }

  .btn-container {
    gap: 15px;
  }
`;

const StyleWrapper = styled.div`
  height: 100%;

  .iframe-container {
    flex: 1;
    position: relative;
    border-right: 4px solid var(--menu-active-color);
    padding-right: 15px;

    iframe {
      width: 100%;
    }

    &::after {
      content: "";
      position: absolute;
      top: 0;
      right: -4px;
      width: 4px;
      height: 50%;
      background-color: var(--red-color);
    }
  }

  .form-container {
    flex: 1;
    margin-left: 30px;
    align-items: stretch;
  }

  .form-tabs {
    position: relative;
  }

  .form-tab-indicator {
    background-color: unset;
  }

  .form-tab-indicator::after {
    content: "";
    position: absolute;
    top: -4px;
    left: 25px;
    width: 40%;
    height: 4px;
    background-color: var(--red-color);
  }

  .form-tab {
    padding-left: 25px;
    padding-right: 20px;
    font-size: 18px;
    color: var(--light-grey-text);
    text-transform: unset;

    &.Mui-selected {
      background-color: var(--light-grey-bg);
      color: var(--text-color);
      font-weight: 700;
    }
  }

  .form-tab-content {
    padding: 20px 25px;

    background-color: var(--light-grey-bg);
  }

  .MuiPaper-root {
    background-color: transparent;
    border: none;
    box-shadow: none;
  }

  .form-tab-content__section {
    margin: 10px 0;
  }

  .accordion-head {
    border-left: 4px solid var(--red-color);
    background-color: var(--white);

    span {
      font-size: 18px;
      font-weight: 700;
    }
  }

  .MuiAccordion-root::before {
    all: unset;
  }

  .MuiButtonBase-root.MuiAccordionSummary-root {
    min-height: unset;
  }

  .Mui-expanded.MuiAccordionSummary-content {
    margin: 12px 0;
  }

  .MuiPaper-root.MuiAccordion-root.Mui-expanded {
    margin-top: 10px;
  }

  .MuiAccordionDetails-root {
    padding-left: 0;
    padding-right: 0;

    .row .col,
    .row.col div {
      &:nth-child(1) {
        padding-left: 0;
      }

      &:nth-child(2) {
        padding-right: 0;
      }
    }
  }

  .row > * {
    padding-left: 0;
  }
`;

export default DocumentForm;
